import styled, { keyframes } from "styled-components";
import { designTokens, getTypography } from "styles/designTokens";

const fadeInSlide = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// RealtimeSttPanel 전용 ContentContainer (overflow 제거)
export const RealtimeContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 32px;
  overflow: hidden;
`;

export const TranscriptContainer = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

export const SummaryCard = styled.div`
  margin-bottom: 16px;
  padding: 20px 22px;
  border-left: 4px solid #5c63ff;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
  animation: ${fadeInSlide} 0.3s ease-out;
`;

export const SummaryHeader = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: ${designTokens.colors.font.primary};
  margin-bottom: 20px;
  ${getTypography("korean", "body1")}
`;

export const TopicContainer = styled.div`
  margin-bottom: 16px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const TopicLabel = styled.div`
  font-weight: 600;
  color: ${designTokens.colors.font.primary};
  margin-bottom: 8px;
  font-size: 14px;
  ${getTypography("korean", "body3")}
`;

export const TopicText = styled.div`
  color: ${designTokens.colors.font.secondary};
  line-height: 1.6;
  font-size: 15px;
  ${getTypography("korean", "body2_1")}
`;

export const SummaryContainer = styled.div`
  margin-bottom: 0;
`;

export const SummaryLabel = styled.div`
  font-weight: 600;
  color: ${designTokens.colors.font.primary};
  margin-bottom: 8px;
  font-size: 14px;
  ${getTypography("korean", "body3")}
`;

export const SummaryText = styled.div`
  color: ${designTokens.colors.font.secondary};
  line-height: 1.8;
  white-space: pre-wrap;
  font-size: 15px;
  ${getTypography("korean", "body2_1")}
`;

export const TranscriptsList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

export const MemoOriginalLayout = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

export const MemoColumn = styled.div`
  flex: 2;
  min-width: 0;
`;

export const OriginalColumn = styled.div`
  flex: 1.4;
  min-width: 0;
`;

export const SectionHeader = styled.div`
  color: ${designTokens.colors.font.primary};
  margin: 16px 0;
  ${getTypography("korean", "body")}
  font-weight: 600;
`;

export const MeetingMemoWrapper = styled.div`
  border: 1px solid #e3e7ff;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
`;

export const MemoEmptyState = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: #8a8fa3;
  border: 1px dashed #dfe3ff;
  border-radius: 12px;
  ${getTypography("korean", "body3")}
`;

export const MemoLoadingState = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 13px;
  color: #6b74ff;
  border-radius: 12px;
  ${getTypography("korean", "body4")}
`;

export const MeetingMemoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
`;

export const MeetingMemoItem = styled.li<{ $isCurrent?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: ${designTokens.colors.font.primary};
  ${getTypography("korean", "body2_2")}

  span {
    word-break: break-word;
    white-space: pre-wrap;
  }

  ${({ $isCurrent }) =>
    $isCurrent &&
    `
      opacity: 0.6;
      font-style: italic;
    `}
`;

export const MeetingBullet = styled.span`
  display: inline-block;
  font-size: 14px;
  margin-top: 2px;
  color: ${designTokens.colors.font.tertiary};
`;

export const TranscriptCard = styled.div<{ $isRefined?: boolean }>`
  margin-bottom: 16px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  animation: ${fadeInSlide} 0.3s ease-out;
  
  ${({ $isRefined }) =>
    $isRefined &&
    `
    border-left: 3px solid #5c63ff;
    box-shadow: 0 2px 8px rgba(92, 99, 255, 0.1);
  `}
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${designTokens.colors.font.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  ${getTypography("korean", "body5")}
`;

export const SimpleTranscript = styled.div`
  margin-bottom: 16px;
  padding: 0;
  color: ${designTokens.colors.font.primary};
  font-size: 14px;
  line-height: 1.8;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  ${getTypography("korean", "body2_2")}
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eceff3;
  margin: 16px 0;
`;

export const OriginalText = styled.div`
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.06);
  color: #555;
  font-size: 14px;
  line-height: 1.8;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  ${getTypography("korean", "body2_2")}
`;

export const OriginalSection = styled.div`
  margin-top: 4px;
  padding: 10px 12px;
  font-size: 12px;
  opacity: 0.85;
  background: #f9fafb;
  border-radius: 10px;
`;

export const OriginalSectionHeader = styled.div`
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: ${designTokens.colors.font.secondary};
  ${getTypography("korean", "body3")}
`;

export const OriginalLogItem = styled.div`
  margin-bottom: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: ${designTokens.colors.font.tertiary};
  word-break: break-word;
  white-space: pre-wrap;
`;

export const RefinedText = styled.div`
  color: ${designTokens.colors.font.primary};
  font-size: 14px;
  line-height: 1.8;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  ${getTypography("korean", "body2_2")}
`;

export const RefinedContent = styled.span`
  flex: 1;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
`;

export const DiffHighlight = styled.span<{ $type: 'added' | 'deleted' }>`
  ${({ $type }) =>
    $type === 'added'
      ? `
    background: #fff3cd;
    color: #856404;
    padding: 2px 4px;
    border-radius: 3px;
    font-weight: 500;
  `
      : `
    background: #f8d7da;
    color: #721c24;
    padding: 2px 4px;
    border-radius: 3px;
    text-decoration: line-through;
    opacity: 0.7;
  `}
`;

export const RefinedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding: 4px 10px;
  background-color: #E8F5E9;
  color: #2E7D32;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  ${getTypography("korean", "body5")}
`;

export const DefaultText = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${designTokens.colors.font.tertiary};
  min-height: 300px;
  ${getTypography("korean", "body2_2")}
`;

export const FinalTranscript = styled.div`
  flex-grow: 1;
  color: ${designTokens.colors.font.primary};
  p {
    margin: 0 0 10px;
    padding: 0;
  }
`;

export const CurrentTranscript = styled.div`
  margin-top: 16px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.02);
  border-left: 3px solid #5c63ff;
  border-radius: 4px;
  color: ${designTokens.colors.font.tertiary};
  font-style: italic;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  ${getTypography("korean", "body3")}
`;

export const OriginalToggle = styled.button`
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: ${designTokens.colors.font.secondary};
  font-size: 12px;
  cursor: pointer;
  align-self: flex-start;
  ${getTypography("korean", "body4")}

  &:hover {
    background: #eef2ff;
    border-color: #c7d2fe;
    color: ${designTokens.colors.font.primary};
  }
`;

export const MemoTag = styled.span`
  font-size: 11px;
  background: #eef1ff;
  padding: 2px 8px;
  border-radius: 6px;
  margin-right: 6px;
  color: ${designTokens.colors.font.secondary};
  flex-shrink: 0;
  ${getTypography("korean", "body5")}
`;

export const OriginalToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

export const OriginalHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0;
`;

export const OriginalTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${designTokens.colors.font.secondary};
  ${getTypography("korean", "body4")}
`;

export const DraftTag = styled.span`
  font-size: 10px;
  background: #eef1ff;
  padding: 2px 6px;
  border-radius: 6px;
  margin-left: 6px;
  color: ${designTokens.colors.font.secondary};
  flex-shrink: 0;
  ${getTypography("korean", "body5")}
`;
